import type { ReactNode } from "react";
import { BenefitsTicker } from "@/features/home/sections/BenefitsTicker/BenefitsTicker";
import { PartnersTicker } from "@/features/home/sections/PartnersTicker/PartnersTicker";
import { useSignupModal } from "@/features/signup/SignupModal";
import { useLocale, useMaestriaContent } from "@/shared/i18n/LocaleContext";
import { HeroVideo } from "./HeroVideo";
import { ButtonLink } from "@/shared/components/ButtonLink/ButtonLink";
import styles from "./Hero.module.css";

function accentuate(text: string, accents: string | readonly string[]) {
  const list = typeof accents === "string" ? [accents] : accents;
  const parts: ReactNode[] = [];
  let rest = text;

  for (const accent of list) {
    const index = rest.indexOf(accent);
    if (index < 0) continue;
    parts.push(rest.slice(0, index), <strong key={accent}>{accent}</strong>);
    rest = rest.slice(index + accent.length);
  }

  parts.push(rest);
  return <>{parts}</>;
}

export function Hero() {
  const { brand, hero } = useMaestriaContent();
  const { locale, setLocale } = useLocale();
  const openSignup = useSignupModal();
  const ellipseClasses = [styles.ellipse1, styles.ellipse2, styles.ellipse3];

  return (
    <section
      id="hero"
      className={`maestria-section ${styles.hero}`}
      aria-labelledby="hero-title"
    >
      <nav
        className={styles.langSelector}
        aria-label={locale === "en" ? "Select language" : "Selecionar idioma"}
      >
        <button
          type="button"
          className={`${styles.langButton} ${locale === "pt" ? styles.langActive : ""}`}
          aria-label="Português"
          aria-pressed={locale === "pt"}
          aria-current={locale === "pt" ? "true" : undefined}
          onClick={() => setLocale("pt")}
        >
          PT
        </button>
        <span className={styles.langDivider} aria-hidden="true">
          |
        </span>
        <button
          type="button"
          className={`${styles.langButton} ${locale === "en" ? styles.langActive : ""}`}
          aria-label="English"
          aria-pressed={locale === "en"}
          aria-current={locale === "en" ? "true" : undefined}
          onClick={() => setLocale("en")}
        >
          EN
        </button>
      </nav>

      <div className={styles.viewport}>
        <div className={styles.stage}>
          <div className={styles.glowStack} aria-hidden="true">
            {hero.ellipses.map((src, index) => (
              <img
                key={src}
                className={ellipseClasses[index]}
                src={src}
                alt=""
              />
            ))}
          </div>

          <img
            className={styles.portraitEdu}
            src={hero.portraitEdu}
            alt={hero.portraitEduAlt}
            width={634}
            height={858}
            fetchPriority="high"
          />

          <img
            className={styles.portrait}
            src={hero.portrait}
            alt={hero.portraitAlt}
            width={864}
            height={689}
            fetchPriority="high"
          />

          <div className={styles.inner}>
            {/* No desktop isso é `display: contents` — o palco do Figma continua
                sendo um bloco só. No celular vira a primeira tela: logo, título
                e o vídeo vertical comendo toda a sobra do viewport. */}
            <div className={styles.firstScreen}>
              <div className={styles.brandBlock}>
                <img
                  className={styles.logo}
                  src={brand.logo}
                  alt={brand.name}
                  width={442}
                  height={102}
                />
                <p className={styles.logoTagline}>{hero.logoTagline}</p>
              </div>

              <h1 id="hero-title" className={styles.title}>
                {accentuate(hero.title, hero.titleAccent)}
              </h1>

              <div className={styles.videoStage}>
                <HeroVideo
                  videoId={hero.videoId}
                  videoIdVertical={hero.videoIdVertical}
                  title={hero.videoThumbAlt}
                />

                <img
                  className={styles.note}
                  src={hero.noteImage}
                  alt={hero.noteAlt}
                  width={342}
                  height={115}
                />
              </div>
            </div>

            <ButtonLink onClick={openSignup} className={styles.cta}>
              {hero.cta.label}
            </ButtonLink>

            <p className={styles.tagline}>
              {accentuate(hero.tagline, hero.taglineAccents)}
            </p>

            <div className={styles.powered}>
              <span>{brand.poweredBy}</span>
              <img src={brand.autou} alt="AutoU" width={119} height={31} />
            </div>
          </div>
        </div>
      </div>

      <BenefitsTicker />
      <PartnersTicker />
      <div className={styles.unveil} aria-hidden="true" />
    </section>
  );
}
